import { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard, Users, Clock, CalendarDays, Receipt, LogOut, User, Building2,
  ClipboardCheck, BadgeCheck, Megaphone, FolderOpen, Award, UserCog, Briefcase,
  IndianRupee, AlertCircle, Tag, MapPin, Building, BarChart3, GraduationCap, TrendingUp, Shield, FileSearch, Boxes, Ticket, Settings, ShieldCheck, Wallet,
  ChevronDown, ChevronRight, Cloud, FolderArchive, Crown, Repeat,
} from 'lucide-react';
import { toast } from 'sonner';

const navItems = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, testId: 'nav-dashboard' },
  { to: '/announcements', label: 'Announcements', icon: Megaphone, testId: 'nav-announcements' },
  { to: '/employees', label: 'Employees', icon: Users, testId: 'nav-employees', roles: ['admin', 'hr', 'reporting_manager'] },
  { to: '/departments', label: 'Departments', icon: Building2, testId: 'nav-departments', roles: ['admin', 'hr'] },
  { to: '/branches', label: 'Branches', icon: MapPin, testId: 'nav-branches', roles: ['admin', 'hr'] },
  { to: '/reports', label: 'Reports', icon: BarChart3, testId: 'nav-reports', roles: ['admin', 'hr'], module: 'reports' },
  { to: '/analytics', label: 'Analytics', icon: TrendingUp, testId: 'nav-analytics', roles: ['admin', 'hr'], module: 'reports' },
  { to: '/admin/roles', label: 'Roles', icon: Shield, testId: 'nav-roles', roles: ['admin'] },
  { to: '/admin/audit-logs', label: 'Audit Logs', icon: FileSearch, testId: 'nav-audit-logs', roles: ['admin', 'it_admin'] },
  { to: '/assets', label: 'Assets', icon: Boxes, testId: 'nav-assets', roles: ['admin', 'it_admin'], module: 'assets' },
  { to: '/tickets', label: 'Tickets', icon: Ticket, testId: 'nav-tickets', module: 'tickets' },
  { to: '/admin/ticket-categories', label: 'Ticket Categories', icon: Settings, testId: 'nav-ticket-categories', roles: ['admin'], module: 'tickets' },
  // Security & Governance is rendered separately as an expandable parent.
  { kind: 'group', key: 'security', label: 'Security & Governance', icon: ShieldCheck,
    testId: 'nav-security-group', roles: ['admin', 'it_admin'], module: 'security',
    children: [
      { to: '/security/entra', label: 'Microsoft Entra', icon: ShieldCheck, testId: 'nav-security-entra' },
      { to: '/security/aws', label: 'AWS Governance', icon: Cloud, testId: 'nav-security-aws' },
      { to: '/security/employee-access', label: 'Employee Access Records', icon: User, testId: 'nav-security-emp-access' },
      { to: '/security/vault', label: 'Report Vault', icon: FolderArchive, testId: 'nav-security-vault' },
    ],
  },
  { to: '/custom-roles', label: 'Custom Roles', icon: Tag, testId: 'nav-custom-roles', roles: ['admin', 'hr'] },
  { to: '/company-profile', label: 'Company Profile', icon: Building, testId: 'nav-company-profile', roles: ['admin', 'hr'] },
  { to: '/recruitment', label: 'Recruitment', icon: Briefcase, testId: 'nav-recruitment', roles: ['admin', 'hr'] },
  { to: '/my-team', label: 'My Team', icon: UserCog, testId: 'nav-my-team', roles: ['admin', 'reporting_manager'] },
  { to: '/approvals', label: 'Approvals', icon: ClipboardCheck, testId: 'nav-approvals', roles: ['admin', 'hr'] },
  { to: '/reviews', label: 'Reviews', icon: Award, testId: 'nav-reviews', hideForRoles: ['payroll_manager', 'it_admin'] },
  { to: '/attendance', label: 'Attendance', icon: Clock, testId: 'nav-attendance', hideForRoles: ['payroll_manager', 'it_admin'], module: 'attendance' },
  { to: '/leaves', label: 'Leaves', icon: CalendarDays, testId: 'nav-leaves', hideForRoles: ['payroll_manager', 'it_admin'] },
  { to: '/payroll', label: 'Payroll', icon: Receipt, testId: 'nav-payroll', module: 'payroll' },
  { to: '/reimbursements', label: 'Reimbursements', icon: IndianRupee, testId: 'nav-reimbursements', module: 'payroll' },
  { to: '/issues', label: 'Report an Issue', icon: AlertCircle, testId: 'nav-issues' },
  { to: '/documents', label: 'Documents', icon: FolderOpen, testId: 'nav-documents', hideForRoles: ['payroll_manager', 'it_admin'] },
  { to: '/training', label: 'Training', icon: GraduationCap, testId: 'nav-training', hideForRoles: ['payroll_manager', 'it_admin'] },
  { to: '/profile', label: 'Profile', icon: User, testId: 'nav-profile' },
];

// Routes that a TERMINATED employee may still access (read-only mode)
const TERMINATED_ALLOWED = new Set(['/dashboard', '/payroll', '/profile', '/issues']);

const roleLabel = {
  admin: 'Admin', hr: 'HR', reporting_manager: 'Reporting Mgr', employee: 'Employee',
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const userRoles = user?.roles || (user?.role ? [user.role] : []);
  // A user is in "onboarding mode" only if they are purely an employee with no other permission role
  const isEmployeeUnverified = userRoles.length > 0 && userRoles.every(r => r === 'employee') && user?.onboarding_status !== 'verified';
  const isTerminated = !!user?.terminated;
  const isSuperAdmin = !!user?.is_super_admin;
  // ---- Per-tenant branding (company name, logo) + plan-based module gating ----
  const tenant = user?.tenant || {};
  const tenantModules = new Set(tenant.modules || []);
  const tenantName = tenant.company_name || 'Lanvaus Private Limited';
  // Brand label: tenant's exact configured name. Optional `display_name` from
  // Company Profile overrides this (e.g. "PROTEINHUNT") if the tenant wants a
  // shorter custom badge. CSS truncates with ellipsis if it overflows the rail.
  const brandShort = (tenant.display_name || tenantName).toUpperCase();
  const tenantLogo = tenant.logo_url || '/lanvaus-logo.png';
  // Super admins must NEVER see per-tenant data — only the platform cockpit and their own profile
  const visibleNavItems = isSuperAdmin
    ? []
    : (isTerminated ? navItems.filter(it => TERMINATED_ALLOWED.has(it.to)) : navItems);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="bg-[#3D4A8E] text-white sticky top-0 z-40 shadow-sm" data-testid="app-header">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => nav(isSuperAdmin ? '/super-admin' : '/dashboard')} data-testid="brand-logo">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden bg-white/10">
              <img src={tenantLogo} alt={tenantName} className="w-full h-full object-contain" />
            </div>
            <div className="leading-tight max-w-[160px]">
              <div className="font-heading font-bold text-base tracking-tight truncate" data-testid="brand-name" title={brandShort}>{brandShort}</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/60">HRMS Portal</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {/* Org switcher — only renders when this email has >1 active tenant memberships */}
            <OrgSwitcher user={user} />
            <div className="hidden md:flex flex-col items-end leading-tight">
              <div className="text-sm font-medium">{user?.name}</div>
              <div className="flex gap-1 mt-0.5" data-testid="user-role-badges">
                {(user?.roles || [user?.role]).map(r => (
                  <span key={r} className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                    r === 'admin' ? 'bg-amber-100 text-amber-700 border-amber-200' :
                    r === 'hr' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' :
                    r === 'reporting_manager' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                    'bg-white/10 text-white border-white/20'
                  }`}>
                    {roleLabel[r] || r}
                  </span>
                ))}
              </div>
            </div>
            <button onClick={logout} data-testid="logout-button"
              className="flex items-center gap-2 bg-white/10 hover:bg-white/20 transition-colors rounded-full px-4 py-2 text-sm">
              <LogOut className="w-4 h-4" strokeWidth={1.8} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6 grid grid-cols-1 lg:grid-cols-[230px_1fr] gap-6">
        <aside className="lg:sticky lg:top-[88px] lg:self-start">
          <nav className="bg-white border border-slate-200 rounded-2xl p-2 shadow-sm space-y-0.5" data-testid="sidebar-nav">
            {/* Platform-owner-only entry (above everything else) */}
            {user?.is_super_admin && (
              <NavLink to="/super-admin" data-testid="nav-super-admin"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2 rounded-xl text-sm font-semibold transition-colors mb-1 ${
                    isActive
                      ? 'bg-gradient-to-r from-amber-500 to-rose-500 text-white shadow-sm'
                      : 'text-amber-700 bg-amber-50/60 hover:bg-amber-100'
                  }`}>
                <Crown className="w-4 h-4" strokeWidth={1.8} /> Super Admin
              </NavLink>
            )}
            {isEmployeeUnverified ? (
              <NavLink to="/onboarding" data-testid="nav-onboarding"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                    isActive ? 'bg-[#3D4A8E] text-white' : 'text-slate-600 hover:bg-slate-50'
                  }`}>
                <BadgeCheck className="w-4 h-4" strokeWidth={1.8} /> Onboarding
              </NavLink>
            ) : (
              visibleNavItems.map(item => {
                const userRoles = user?.roles || [user?.role];
                if (item.roles && !item.roles.some(r => userRoles.includes(r))) return null;
                // ⛔ Plan-based gating: hide items whose `module` isn't in the tenant's plan.
                // Super-admin and tenant-admin always see everything regardless of plan, BUT
                // the user explicitly wanted plan limits enforced — so we honour modules
                // for ALL roles. (Modules are e.g. "security", "payroll", "tickets", "assets".)
                if (item.module && tenantModules.size > 0 && !tenantModules.has(item.module)) return null;
                // hideForRoles: hide if the user holds ANY of these roles (and no broader role).
                // Admin is never hidden — they are global.
                if (item.hideForRoles && !userRoles.includes('admin')
                    && userRoles.every(r => item.hideForRoles.includes(r) || r === 'employee')
                    && userRoles.some(r => item.hideForRoles.includes(r))) return null;
                if (item.kind === 'group') {
                  return <SidebarGroup key={item.key} item={item} />;
                }
                const Icon = item.icon;
                return (
                  <NavLink key={item.to} to={item.to} data-testid={item.testId}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                        isActive ? 'bg-[#3D4A8E] text-white' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                      }`}>
                    <Icon className="w-4 h-4" strokeWidth={1.8} /> {item.label}
                  </NavLink>
                );
              })
            )}
          </nav>
          <div className="mt-4 hidden lg:block text-xs text-slate-400 px-2">
            &copy; {new Date().getFullYear()} {tenantName}
          </div>
        </aside>
        <main className="min-w-0 animate-fade-in-up">
          {isTerminated && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-2xl px-5 py-3 text-sm text-red-800" data-testid="terminated-banner">
              <b>Your employment has been terminated.</b> You can view your past payslips and report issues to HR. Please contact HR for any other queries.
            </div>
          )}
          {children}
        </main>
      </div>
    </div>
  );
}

// ----- Expandable parent menu (Security & Governance) -----
function SidebarGroup({ item }) {
  const Icon = item.icon;
  const location = useLocation();
  const childActive = (item.children || []).some(c => location.pathname.startsWith(c.to));
  const storageKey = `sidebar-group:${item.key}`;
  const [open, setOpen] = useState(() => {
    const stored = localStorage.getItem(storageKey);
    return stored === null ? childActive : stored === '1';
  });
  // Auto-open when navigating into a child (don't auto-close).
  useEffect(() => {
    if (childActive && !open) {
      setOpen(true);
      localStorage.setItem(storageKey, '1');
    }
  }, [childActive, open, storageKey]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    localStorage.setItem(storageKey, next ? '1' : '0');
  };

  return (
    <div data-testid={item.testId}>
      <button
        type="button"
        onClick={toggle}
        data-testid={`${item.testId}-toggle`}
        className={`w-full flex items-center justify-between gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
          childActive ? 'text-slate-900 bg-slate-100' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
        }`}
      >
        <span className="flex items-center gap-3">
          <Icon className="w-4 h-4" strokeWidth={1.8} /> {item.label}
        </span>
        {open
          ? <ChevronDown className="w-4 h-4 text-slate-400" strokeWidth={1.8} />
          : <ChevronRight className="w-4 h-4 text-slate-400" strokeWidth={1.8} />}
      </button>
      {open && (
        <div className="ml-3 mt-0.5 pl-3 border-l border-slate-200 space-y-0.5"
             data-testid={`${item.testId}-children`}>
          {item.children.map(c => {
            const CIcon = c.icon;
            return (
              <NavLink key={c.to} to={c.to} data-testid={c.testId}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-[13px] font-medium transition-colors ${
                    isActive ? 'bg-[#3D4A8E] text-white' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'
                  }`}>
                <CIcon className="w-3.5 h-3.5" strokeWidth={1.8} /> {c.label}
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ------------------------------------------------------------------
// Organization switcher — shown in the top-right header only when the
// signed-in email has memberships in ≥2 tenants (current + at least one
// other). Lets the user hot-swap to another tenant without re-login.
// ------------------------------------------------------------------
function OrgSwitcher({ user }) {
  const { switchTenant } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const others = user?.available_tenants || [];
  if (!others.length) return null;
  const current = {
    company_name: user?.tenant?.company_name || 'Current',
    logo_url: user?.tenant?.logo_url,
  };
  const onSwitch = async (tenantId, name) => {
    setBusy(true);
    try {
      await switchTenant(tenantId);
      toast.success(`Switched to ${name}`);
      setOpen(false);
      nav('/dashboard');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not switch organization');
    } finally { setBusy(false); }
  };
  return (
    <div className="relative" data-testid="org-switcher">
      <button onClick={() => setOpen(!open)} disabled={busy}
              className="flex items-center gap-2 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-full text-sm transition"
              data-testid="org-switcher-button">
        <Repeat className="w-4 h-4" strokeWidth={2} />
        <span className="hidden md:inline">Switch org</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-72 bg-white rounded-2xl shadow-xl border border-slate-200 z-50 overflow-hidden">
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Currently signed in to</div>
              <div className="flex items-center gap-2 mt-1">
                {current.logo_url ? <img src={current.logo_url} className="w-6 h-6 rounded object-contain" alt=""/> : <Building2 className="w-5 h-5 text-slate-400"/>}
                <span className="text-sm font-semibold text-slate-900 truncate">{current.company_name}</span>
              </div>
            </div>
            <div className="py-1 max-h-72 overflow-auto">
              <div className="px-4 pt-2 pb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Switch to</div>
              {others.map(t => (
                <button key={t.tenant_id} onClick={() => onSwitch(t.tenant_id, t.company_name)} disabled={busy}
                        data-testid={`org-switch-${t.tenant_id}`}
                        className="w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-slate-50 transition disabled:opacity-50">
                  <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {t.logo_url ? <img src={t.logo_url} className="w-full h-full object-contain" alt=""/> : <Building2 className="w-4 h-4 text-slate-400"/>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{t.company_name}</div>
                    <div className="text-[11px] text-slate-500 flex items-center gap-1.5 mt-0.5">
                      <span className={`inline-block w-1.5 h-1.5 rounded-full ${t.status === 'Active' ? 'bg-emerald-500' : 'bg-slate-400'}`}></span>
                      {t.status}
                      {t.role && <span className="capitalize">· {(t.role || '').replace('_',' ')}</span>}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
